import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});

// 토큰 자동 첨부 + 페이지 경로 전송
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('hire_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (typeof window !== 'undefined') {
    config.headers['X-Page'] = window.location.pathname;
  }
  return config;
});

// 401 응답 시 토큰 삭제 + 로그인 페이지 이동
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('hire_token');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

// 공개 API (인증 불필요)
export const publicApi = {
  getRecruitmentStatus: () =>
    api.get('/auth/recruitment-status').then((res) => res.data),
};

// 인증 관련 API
export const authApi = {
  register: (name: string, email: string, password: string, passwordConfirm: string, phone: string, campaign_id?: number, privacy_agreed?: boolean, privacy_retention_agreed?: boolean) =>
    api.post('/auth/register', { name, email, password, passwordConfirm, phone, privacy_agreed, privacy_retention_agreed, ...(campaign_id ? { campaign_id } : {}) }).then((res) => res.data),

  verifyCode: (email: string, code: string) =>
    api.post('/auth/verify-code', { email, code }).then((res) => res.data),

  login: (email: string, password: string, campaignId?: number) =>
    api.post('/auth/login', { email, password, campaign_id: campaignId }).then((res) => res.data),

  resetPassword: (email: string, name: string, phone: string) =>
    api.post('/auth/reset-password', { email, name, phone }).then((res) => res.data),

  verifyResetCode: (email: string, code: string) =>
    api.post('/auth/reset-password/verify', { email, code }).then((res) => res.data),

  changePassword: (currentPassword: string, newPassword: string, newPasswordConfirm: string) =>
    api.put('/auth/change-password', { currentPassword, newPassword, newPasswordConfirm }).then((res) => res.data),

  // 현재 지원자 정보 조회
  getMe: () =>
    api.get('/auth/me').then((res) => res.data),

  // 테스트 계정 상태 변경
  updateTestStatus: (status: string) =>
    api.put('/auth/test-status', { status }).then((res) => res.data),
};

// 캠페인 포지션 API (지원자용)
export const campaignApi = {
  // 캠페인별 모집 포지션 목록 조회
  getCampaignPositions: (campaignId: number) =>
    api.get(`/auth/campaigns/${campaignId}/positions`).then((res) => res.data),
};

// 지원서 관련 API
export const applicationApi = {
  // 내 지원서 조회
  getMyApplication: () =>
    api.get('/application/me').then((res) => res.data),

  // 지원서 저장 (임시저장)
  save: (data: any) =>
    api.put('/application', data).then((res) => res.data),

  // 지원서 제출
  submit: () =>
    api.post('/application/submit').then((res) => res.data),

  // 포지션 목록 조회
  getPositions: () =>
    api.get('/application/positions').then((res) => res.data),

  // 이력서 업로드
  uploadResume: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/application/resume', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data);
  },
};

// 면접 API (지원자용)
export const interviewApi = {
  // 면접 정보 조회 (시험결과 + 면접 + proposals)
  getInterviewInfo: (applicantId: number) =>
    api.get(`/interview/info/${applicantId}`).then((res) => res.data),

  // 제안된 일정 선택
  selectDate: (interviewId: number, proposalId: number, selectedIndex: number) =>
    api.post('/interview/select-date', {
      interview_id: interviewId,
      proposal_id: proposalId,
      selected_index: selectedIndex,
    }).then((res) => res.data),

  // 대안 일정 제시
  proposeDates: (interviewId: number, proposedDates: { date: string; time: string }[], message?: string) =>
    api.post('/interview/propose-dates', {
      interview_id: interviewId,
      proposed_dates: proposedDates,
      message,
    }).then((res) => res.data),

  // 면접 취소
  cancelInterview: (interviewId: number) =>
    api.put(`/interview/cancel/${interviewId}`).then((res) => res.data),

  // 재면접 신청
  requestReinterview: (interviewId: number) =>
    api.post(`/interview/reinterview/${interviewId}`).then((res) => res.data),
};

// 오퍼 API (지원자용)
export const offerApi = {
  getMyOffer: () =>
    api.get('/application/offer').then((res) => res.data),
  acceptOffer: () =>
    api.post('/application/offer/accept').then((res) => res.data),
  rejectOffer: () =>
    api.post('/application/offer/reject').then((res) => res.data),
  updateRequestedSalary: (requested_salary: number) =>
    api.put('/application/requested-salary', { requested_salary }).then((res) => res.data),
  confirmStartDate: (start_date: string) =>
    api.put('/application/offer/start-date', { start_date }).then((res) => res.data),
};

// 온보딩 서류 API
export const onboardingApi = {
  getMyOnboarding: () =>
    api.get('/application/onboarding').then((res) => res.data),

  uploadDocument: (docType: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('docType', docType);
    return api.post('/application/onboarding/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data);
  },
};

// 서면 평가 API
export const writtenTestApi = {
  // 내 시험 목록 조회 (applicant_id는 서버에서 토큰으로 식별)
  getMyTests: () =>
    api.get('/written-test/my').then((res) => res.data),

  // 시험 시작
  startTest: (testId: number) =>
    api.post('/written-test/start', { test_id: testId }).then((res) => res.data),

  // 답변 저장
  saveAnswer: (applicantTestId: number, questionId: number, answer: string) =>
    api.post('/written-test/answer', {
      applicant_test_id: applicantTestId,
      question_id: questionId,
      answer,
    }).then((res) => res.data),

  // 시험 제출
  submitTest: (applicantTestId: number) =>
    api.post('/written-test/submit', {
      applicant_test_id: applicantTestId,
    }).then((res) => res.data),
};

// 평판조회 동의서 API
export const referenceConsentApi = {
  getMyConsent: () =>
    api.get('/application/reference-consent').then((res) => res.data),

  submitConsent: (data: {
    personal: any;
    references: any[];
    signature_image: string;
  }) =>
    api.post('/application/reference-consent', data).then((res) => res.data),
};

export default api;
